import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { EventId } from '@interfaces';
import { AccountTypes, FlowTypes, RejectedAccount, urlMapper } from '@interfaces/models';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import {
  closeSpinner,
  External,
  Go,
  LogError,
  LogInfo,
  State,
  TrackProcessError,
  UpdateDisplayedAccounts,
} from '@store/index';
import {
  selectClientAccountsLoaded,
  selectClientHasVCAccounts,
  selectEligibleInternalTransferFromAccountsWithRejects,
  selectEligibleInternalTransferToAccountsWithRejects,
  shouldOrgTransferContinue,
} from '@store/selectors/client-accounts/client-accounts.selectors';
import { selectInternalTransferEligibility } from '@store/selectors/eligibility/internal-transfer/internal-transfer.eligibility.selectors';
import {
  selectNonVCOrgTransferToggle,
  selectVCOrgToOrgToggle,
} from '@store/selectors/feature/feature.selectors';
import {
  selectInternalTransferFromAccountType,
  selectInternalTransferToAccountType,
} from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, map, Observable, of, take, withLatestFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InternalTransferEligibilityGuard implements CanActivate {
  rejectedFromAccounts: RejectedAccount[] = [];
  rejectedToAccounts: RejectedAccount[] = [];
  constructor(
    private globalStore: Store<State>,
    private cookieService: CookieService,
  ) {}
  canActivate(): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }
    return this.globalStore.pipe(
      // Ensure that the client accounts are loaded before proceeding with eligibility check
      select(selectClientAccountsLoaded),
      filter((loaded) => loaded),
      take(1),
      // Check the client's eligibility with data from the store, and return that along with the from accounts
      withLatestFrom(
        this.globalStore.select(selectInternalTransferEligibility),
        this.globalStore.select(
          selectEligibleInternalTransferToAccountsWithRejects(this.rejectedToAccounts),
        ),
        this.globalStore.select(
          selectEligibleInternalTransferFromAccountsWithRejects(this.rejectedFromAccounts),
        ),
        this.globalStore.select(selectInternalTransferToAccountType),
        this.globalStore.select(selectInternalTransferFromAccountType),
        this.globalStore.select(selectNonVCOrgTransferToggle),
        this.globalStore.select(selectVCOrgToOrgToggle),
        this.globalStore.select(selectClientHasVCAccounts),
      ),
      map(
        ([
          ,
          payload,
          ,
          ,
          accountToType,
          accountFromType,
          nonVcOrgTransferToggle,
          vcOrgToOrgToggle,
          clientHasVCAccounts,
        ]) => {
          // ORG-from Intelledox routing — moved here from the Summary Continue
          // effect so the decision only runs after `/secure/client-accounts` has
          // resolved (client's VC-account ownership is required for the check).
          //   NON_VC ON                              → continue in the flow
          //   NON_VC OFF, VC ON, client has VC       → continue in the flow
          //   Everything else (both OFF, or VC-only ON without VC accounts) → Intelledox
          if (
            accountFromType === AccountTypes.ORG &&
            !shouldOrgTransferContinue(
              nonVcOrgTransferToggle,
              vcOrgToOrgToggle,
              clientHasVCAccounts,
            )
          ) {
            const config = getConfigClientFromRTE();
            const intelledoxURL =
              config.pretty_ecs_domain + config.internal_transfer_intelledox_endpoint;
            this.globalStore.dispatch(
              LogInfo({
                payload: {
                  eventId: 'internal-transfer-guard--org-routed-to-intelledox',
                  message: `Internal Transfer Eligibility Guard routed ORG-from to Intelledox for ${FlowTypes.INTERNAL_TRANSFER}`,
                  code: LoggerCodes.GENERAL_SUCCESS,
                  data: {
                    nonVcOrgTransferToggle,
                    vcOrgToOrgToggle,
                    clientHasVCAccounts,
                  },
                },
              }),
            );
            this.globalStore.dispatch(External({ url: intelledoxURL }));
            this.globalStore.dispatch(closeSpinner());
            return false;
          }
          // If the client is eligible, update display accounts + log + close loading spinner and then activate page
          if (payload.isInternalTransferEligible) {
            if (!payload.fromAccounts) {
              throw new Error('BAD_PAYLOAD_FROM_ACCOUNTS');
            }
            this.globalStore.dispatch(
              UpdateDisplayedAccounts({
                payload: payload.fromAccounts,
              }),
            );
            this.globalStore.dispatch(
              LogInfo({
                payload: {
                  eventId: 'internal-transfer-guard--eligible-accounts-found',
                  message: `Internal Transfer Eligibility Guard eligibility success ${FlowTypes.INTERNAL_TRANSFER} ${payload.accountFromType} account`,
                  eligibleFromType: payload.accountFromType,
                  code: LoggerCodes.GENERAL_SUCCESS,
                },
              }),
            );
            this.globalStore.dispatch(closeSpinner());
            this.logIneligibleAccounts(
              'INELIGIBLE_ACCOUNTS_FROM',
              FlowTypes.INTERNAL_TRANSFER,
              payload.accountFromType,
              accountToType,
              this.rejectedFromAccounts,
            );
            this.logIneligibleAccounts(
              'INELIGIBLE_ACCOUNTS_TO',
              FlowTypes.INTERNAL_TRANSFER,
              payload.accountFromType,
              accountToType,
              this.rejectedToAccounts,
            );
            // If the client is ineligible, go to the ineligible page along with logging/tracking error
          } else {
            this.globalStore.dispatch(
              Go({ path: [`${urlMapper[FlowTypes.INTERNAL_TRANSFER]}/ineligible`] }),
            );

            let message = 'No eligible internal transfer accounts found';

            switch (true) {
              case !!payload.ineligibleFromAccountType && !!payload.ineligibleToAccountType:
                this.globalStore.dispatch(
                  TrackProcessError({
                    errorCode: `coo-it-ineligible-both-${payload.ineligibleFromAccountType}-${payload.ineligibleToAccountType}_account`,
                  }),
                );
                message = `No eligible IT ${payload.ineligibleToAccountType} TO account nor ${payload.ineligibleFromAccountType} FROM account`;
                break;
              case !!payload.ineligibleFromAccountType:
                this.globalStore.dispatch(
                  TrackProcessError({
                    errorCode: `coo-it-ineligible-from-${payload.ineligibleFromAccountType}_account`,
                  }),
                );
                message = `No eligible IT ${payload.ineligibleFromAccountType} FROM account`;
                break;
              case !!payload.ineligibleToAccountType:
                this.globalStore.dispatch(
                  TrackProcessError({
                    errorCode: `coo-it-ineligible-to-${payload.ineligibleToAccountType}_account`,
                  }),
                );
                message = `No eligible IT ${payload.ineligibleToAccountType} TO account`;
                break;
            }

            this.globalStore.dispatch(
              LogError({
                payload: {
                  eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                  message,
                  ineligibleFromAccountType: payload.ineligibleFromAccountType,
                  ineligibleToAccountType: payload.ineligibleToAccountType,
                  flowType: FlowTypes.INTERNAL_TRANSFER,
                  rejectedFromAccounts: this.maskRejectedAccountsPII(this.rejectedFromAccounts),
                  rejectedToAccounts: this.maskRejectedAccountsPII(this.rejectedToAccounts),
                },
              }),
            );
          }
          return payload.isInternalTransferEligible;
        },
      ),
      catchError((err) => {
        this.globalStore.dispatch(Go({ path: ['error'] }));
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: 'internal-transfer-guard--unknown-error',
              message: `Internal Transfer Eligibility Guard failed due to unknown error. ${err.message}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        );
        this.globalStore.dispatch(
          TrackProcessError({
            errorCode: `coo-it-ineligible-app_error`,
          }),
        );

        return of(false);
      }),
    );
  }

  private maskRejectedAccountsPII(
    rejectedAcccounts: RejectedAccount[],
  ): Pick<RejectedAccount, 'accountNumber' | 'accountType' | 'rejectionReason'>[] {
    return rejectedAcccounts.map((account) => ({
      accountNumber: account.accountNumber.slice(-4).padStart(account.accountNumber.length, '*'),
      accountType: account.accountType,
      rejectionReason: account.rejectionReason,
    }));
  }

  private logIneligibleAccounts(
    eventId: string,
    flowType: FlowTypes,
    selectedFromType: AccountTypes | null | undefined,
    selectedToType: AccountTypes | null,
    rejectedAccounts: RejectedAccount[],
  ): void {
    if (rejectedAccounts.length === 0) return;

    const batchSize = 200;
    const totalRejected = rejectedAccounts.length;
    const totalBatches = Math.ceil(totalRejected / batchSize);
    const maskedAccounts = this.maskRejectedAccountsPII(rejectedAccounts);

    for (let i = 0; i < totalBatches; i++) {
      this.globalStore.dispatch(
        LogInfo({
          payload: {
            eventId,
            message: `Ineligible accounts logged for ${eventId}`,
            flowType,
            code: LoggerCodes.GENERAL_SUCCESS,
            data: {
              selectedFromType: selectedFromType || '',
              selectedToType: selectedToType || '',
              totalRejectedCount: totalRejected,
              batchNumber: i + 1,
              totalBatches,
              rejectedAccounts: maskedAccounts.slice(i * batchSize, (i + 1) * batchSize),
            },
          },
        }),
      );
    }
  }
}
